/**
 * global-error.tsx — Next 16 top-level error boundary.
 *
 * Catches errors thrown in the root layout that other error.tsx files
 * couldn't catch (root layout, layout group boundaries). Fires once per
 * uncaught render error.
 *
 * We use this as the React-error-boundary integration for browser-loop:
 * every caught error becomes a `react.error_boundary` event with the
 * full component stack. Source-map resolution happens at forensics-
 * read time on the backend.
 *
 * Note: `unhandled_rejection` + `js.error` are caught by
 * patches/errors.ts via window listeners. This file specifically
 * catches React render-tree errors that don't bubble to window.error.
 */

"use client";

import { useEffect } from "react";

import { emit, BROWSER_EVENT_KIND, SEVERITY } from "@/shared/lib/browser-loop";

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    try {
      emit({
        type: BROWSER_EVENT_KIND.react_error_boundary,
        severity: SEVERITY.error,
        error_message: error.message,
        error_name: error.name,
        stack: error.stack ?? null,
        boundary_name: "global-error",
        // Next.js stamps a digest on server-thrown errors that lets you
        // correlate to backend logs without exposing the full message.
        digest: error.digest ?? null,
      });
    } catch {
      /* never poison the host app */
    }
  }, [error]);

  return (
    <html lang="en">
      <body>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            fontFamily:
              "ui-sans-serif, system-ui, -apple-system, sans-serif",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
            Something went wrong
          </h1>
          <p style={{ color: "#666", marginBottom: "1.5rem" }}>
            {error.message}
          </p>
          <button
            onClick={reset}
            style={{
              padding: "0.5rem 1.5rem",
              borderRadius: "0.375rem",
              border: "1px solid #ccc",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
